import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AssignmentReactiveFormService } from '../../../services/assignment-reactive-form.service';
import { AssignmentConstants } from 'src/app/utils/constants/self-serve/assignment.constants';
import { debounceTime, distinctUntilChanged, take, Subject } from 'rxjs';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/requests/iassignment';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { toIsoDateString } from 'src/app/utils/date-parser.util';
import { AssignmentInformationViewComponent } from 'src/app/pages/self-serve/simulation-builder/components/assignment-information-view/assignment-information-view.component';
import { DetailsSectionComponent } from '../../../components/details-container/details-section.component';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'assignment-information',
  standalone: true,
  templateUrl: './assignment-information.component.html',
  styleUrls: ['./assignment-information.component.scss'],
  imports: [AssignmentInformationViewComponent, DetailsSectionComponent, TranslocoModule],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AssignmentInformationComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  callUuid: string = '';
  assignmentTypeOptions: { label: string; value: AssignmentTypeEnum }[] = [];
  isNewAssignment: boolean = false;

  private valueChangeSubject = new Subject<any>();
  private hasProcessedInitialValue = false;

  constructor(
    private route: ActivatedRoute,
    private assignmentFormService: AssignmentReactiveFormService,
    private assignmentsService: AssignmentsService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      this.assignmentTypeOptions = [
        { label: this.translocoService.translate('simulationBuilder.ASSIGNMENT_TYPE_CERTIFICATION'), value: AssignmentTypeEnum.CERTIFICATION },
        { label: this.translocoService.translate('simulationBuilder.ASSIGNMENT_TYPE_PRACTICE'), value: AssignmentTypeEnum.PRACTICE },
        { label: this.translocoService.translate('simulationBuilder.ASSIGNMENT_TYPE_PERSONALIZED_PRACTICE'), value: AssignmentTypeEnum.PERSONALIZED_PRACTICE },
      ];
    });
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.assignmentFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.form = form.get(AssignmentConstants.DETAILS_SECTION) as FormGroup;
        this.setupValueChangeSubscription();
      });
  }

  ngOnDestroy(): void {
    this.valueChangeSubject.complete();
  }

  private setupValueChangeSubscription(): void {
    this.valueChangeSubject
      .pipe(
        debounceTime(AssignmentConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((formValue) => {
        this.processFormValueChange(formValue);
      });
  }

  onFormValueChange(formValue: any): void {
    this.valueChangeSubject.next(formValue);
  }

  private processFormValueChange(formValue: any): void {
    if (!this.hasProcessedInitialValue) {
      this.hasProcessedInitialValue = true;
      return;
    }

    const endDateValid = AssignmentReactiveFormService.getControlValidity(
      this.form.get('endDate')
    );
    const scoreCriteriaValid = AssignmentReactiveFormService.getControlValidity(
      this.form.get('scoreCriteria')
    );

    if (!endDateValid || !scoreCriteriaValid) {
      return;
    }

    // Use getRawValue() to include disabled controls
    const updatedDetailsValue = this.form.getRawValue();
    this.assignmentFormService.patchForm(
      this.callUuid,
      AssignmentConstants.DETAILS_SECTION,
      updatedDetailsValue
    );
    this.sendRequestToUpdateAssignment(updatedDetailsValue);
  }

  private sendRequestToUpdateAssignment(assignmentData: IAssignment): void {
    const dataToSend = {
      ...assignmentData,
      startDate: toIsoDateString(assignmentData.startDate),
      endDate: toIsoDateString(assignmentData.endDate),
      showMissedFacts: assignmentData.showMissedFacts,
    };
    this.assignmentsService
      .updateAssignment(this.callUuid, dataToSend)
      .subscribe({
        next: (res) => {
          console.log('Assignment updated successfully:', res);
        },
        error: (err) => {
          console.error('Error updating assignment:', err);
        },
      });
  }
}
